import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import {
  applyMapping,
  headerOptions,
  isMappingComplete,
  missingRequiredFields,
  suggestColumnMapping,
  type ColumnMapping,
} from '../../lib/leadBot/listingMapping';
import { formatWhen } from '../../lib/leadBot/queueView';
import { LISTING_FIELDS, type ListingFieldKey } from '../../types/vehicleListing';
import { getStock, uploadStock } from './api';
import { parseStockFile, type ParsedStockFile } from './listingUploadParse';

// SMS Lead Bot Phase 1 — the stock upload, in bot settings alongside the
// knowledge base. Per ACCOUNT, because stock belongs to the business.
//
// ⚠️ UPLOAD, NOT A SCHEDULED FETCH. Feeds differ per client so a mapping step
// is needed regardless, and a manual upload removes a whole class of
// scheduled-job failure from a feature that is not proven yet.
//
// ⚠️ THE UPLOAD DATE IS SHOWN PROMINENTLY. Stock is stale by construction, and
// staff need to know how old it is before they trust what the bot is saying
// about it — the prompt carries the same date for the same reason.

export function StockSection({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedStockFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<ColumnMapping>({});

  const { data, isLoading } = useQuery({
    queryKey: ['lead-bot', 'stock', accountId],
    queryFn: () => getStock(accountId),
  });

  const reset = () => {
    setParsed(null);
    setFileName('');
    setMapping({});
    if (fileInput.current) fileInput.current.value = '';
  };

  const upload = useMutation({
    mutationFn: () => uploadStock(accountId, applyMapping(mapping, parsed!.records)),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-bot', 'stock', accountId] });
      toast.success(
        `Stock replaced — ${result.created} new, ${result.updated} updated, ` +
          `${result.deactivated} no longer listed.`,
      );
      // ⚠️ Skipped rows are REPORTED, never silently dropped: a row the upload
      // could not read is a vehicle the bot will not know about.
      if (result.skipped.length > 0) {
        toast.warning(
          `${result.skipped.length} row${result.skipped.length === 1 ? '' : 's'} could not be ` +
            `read: ${result.skipped
              .slice(0, 3)
              .map((s) => `row ${s.rowNumber} (${s.reason})`)
              .join(', ')}${result.skipped.length > 3 ? '…' : ''}`,
          { duration: 12_000 },
        );
      }
      reset();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleFile = async (file: File) => {
    try {
      const result = await parseStockFile(file);
      if (result.headers.length === 0) {
        toast.error('We could not read any columns from that file.');
        return;
      }
      setParsed(result);
      setFileName(file.name);
      // ⚠️ A SUGGESTION, not a decision. An unfamiliar header is left unmapped
      // for the AM to set rather than guessed at.
      setMapping(suggestColumnMapping(result.headers));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That file could not be read.');
    }
  };

  const missing = parsed ? missingRequiredFields(mapping) : [];
  const ready = parsed !== null && isMappingComplete(mapping);
  const mappedRowCount = parsed ? applyMapping(mapping, parsed.records).length : 0;

  return (
    <div className="space-y-4">
      {/* current state */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="rounded-md border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">
                {data?.summary.activeCount ?? 0} vehicle
                {(data?.summary.activeCount ?? 0) === 1 ? '' : 's'} in stock
                {(data?.summary.inactiveCount ?? 0) > 0 && (
                  <span className="ml-1 text-muted-foreground">
                    ({data!.summary.inactiveCount} no longer listed)
                  </span>
                )}
              </p>
              {/* ⚠️ Prominent, because the bot's answers are only as fresh as
                  this and it will tell customers "as of" this date. */}
              <p className="mt-0.5 text-xs text-muted-foreground">
                {data?.summary.lastUploadedAt
                  ? `Last uploaded ${formatWhen(data.summary.lastUploadedAt)}`
                  : 'No stock has been uploaded yet.'}
              </p>
            </div>
            {(data?.summary.listingsWithStrippedClaims ?? 0) > 0 && (
              // ⚠️ SHOWN, not silent. A stripper that has quietly stopped
              // working looks identical to a feed with no claims in it.
              <Badge variant="warning" title="Licence, towing and weight claims are removed before the bot ever sees them">
                {data!.summary.listingsWithStrippedClaims} with claims removed
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* the legal note — this is why the feature is shaped the way it is */}
      <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Descriptions are cleaned before the bot reads them: any sentence mentioning{' '}
          <strong>licence, towing, weight or GVM</strong> is removed. A listing that says "you
          only need a car licence to drive it" is making a legal claim that depends on the
          specific vehicle and on the customer's own licence — and quoted by text message, that
          reads as advice. Those questions are always handed to a person.
        </p>
      </div>

      {/* upload */}
      {parsed === null ? (
        <div>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Button variant="outline" onClick={() => fileInput.current?.click()}>
            <Upload className="h-4 w-4" />
            Upload a stock file
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">
            CSV or Excel. You choose which column means what after it is read — the file does not
            need particular headers.
          </p>
        </div>
      ) : (
        <div className="space-y-3 rounded-md border p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">
              {fileName} — {parsed.rowCount} row{parsed.rowCount === 1 ? '' : 's'}
            </p>
            <Button variant="ghost" size="sm" onClick={reset}>
              Choose a different file
            </Button>
          </div>

          <div className="space-y-2">
            {LISTING_FIELDS.map((field) => (
              <div key={field.key} className="grid gap-2 sm:grid-cols-[12rem_1fr] sm:items-center">
                <label className="text-sm" htmlFor={`map-${field.key}`}>
                  {field.label}
                  {field.required && <span className="ml-1 text-rose-600">*</span>}
                </label>
                <div>
                  <SelectField
                    id={`map-${field.key}`}
                    value={mapping[field.key] ?? ''}
                    onValueChange={(value) =>
                      setMapping((m) => ({ ...m, [field.key as ListingFieldKey]: value }))
                    }
                    options={headerOptions(parsed.headers)}
                  />
                  {field.hint !== '' && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{field.hint}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {missing.length > 0 && (
            // ⚠️ REPORTED, never guessed. Picking the closest header for a
            // required field is how a description column ends up holding the
            // stock id and every listing is silently wrong.
            <p className="text-sm text-amber-700">
              Still to map: {missing.join(', ')}.
            </p>
          )}

          {/* ⚠️ The consequence, stated before the click rather than after. */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
            <p className="text-xs text-muted-foreground">
              This <strong>replaces</strong> the current stock list. Vehicles missing from this
              file stop being offered — they are kept, not deleted, so past conversations still
              make sense.
            </p>
            <Button onClick={() => upload.mutate()} disabled={!ready || upload.isPending}>
              {upload.isPending ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              Replace stock with {mappedRowCount} vehicle{mappedRowCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
